import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  Calculator,
  GitCompare,
  Bot,
  FileText,
  LogOut,
  Menu,
  X,
  LayoutGrid,
  Building2,
  Users,
  Activity,
} from 'lucide-react';

const USER_NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/calculator', icon: Calculator, label: 'Calculator' },
  { to: '/compare', icon: GitCompare, label: 'Compare' },
  { to: '/advisor', icon: Bot, label: 'AI Advisor' },
  { to: '/reports', icon: FileText, label: 'Reports' },
];

const ADMIN_NAV = [
  { to: '/admin', icon: LayoutGrid, label: 'Overview', end: true },
  { to: '/admin/companies', icon: Building2, label: 'Companies' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/activity', icon: Activity, label: 'Activity' },
];

const TopNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = user?.role === 'admin' ? ADMIN_NAV : USER_NAV;

  return (
    <header className="topnav" data-testid="topnav">
      <div className="topnav-inner">
        {/* Brand */}
        <div className="topnav-brand">
          <img
            src="/ecotech-logo.png"
            alt="EcoTech"
            className="brand-logo"
          />
        </div>

        {/* Primary nav (desktop) */}
        <nav className="topnav-links" aria-label="Main">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `topnav-link ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-[16px] h-[16px]" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User cluster (desktop) */}
        <div className="topnav-user">
          <div className="user-chip" title={user?.email}>
            <div className="user-avatar">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-meta">
              <span className="user-name">{user?.full_name || 'User'}</span>
              <span className="user-email">{user?.email}</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="logout-btn"
            data-testid="logout-button"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Mobile menu trigger */}
        <button
          className="topnav-burger"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="topnav-mobile" data-testid="mobile-menu">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `topnav-mobile-link ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <div className="topnav-mobile-divider" />
          <div className="topnav-mobile-user">
            <div className="user-avatar">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-[var(--c-ink)]">
                {user?.full_name || 'User'}
              </p>
              <p className="text-[11px] truncate text-[var(--c-ink-3)]">
                {user?.email}
              </p>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopNav;
